import type {
  DiscountScope,
  GetPlanSelectorDataQuery,
} from '../../../gql/graphql'

import { DiscountScopeValues } from '../types'
import type { PlanCalculation } from '../types'

// Extract discount types from the query result that include aliased fields
export type QueryDiscount = NonNullable<
  GetPlanSelectorDataQuery['planSelectorData']
>['discounts'][number]

/**
 * Enhanced plan calculation that includes discount information
 */
export interface DiscountedPlanCalculation extends PlanCalculation {
  discountValue?: number
  originalPrice?: number
}

/**
 * Filter discounts by scope (customer or subscription level)
 * @param discounts - Array of discounts to filter
 * @param scope - Scope to filter by
 * @returns Array of discounts matching the specified scope
 */
export function filterDiscountsByScope(
  discounts: QueryDiscount[],
  scope: DiscountScope,
): QueryDiscount[] {
  return discounts.filter((discount) => discount.scope === scope)
}

/**
 * Get all customer-level discounts from a discount array
 * @param discounts - Array of discounts
 * @returns Array of customer-level discounts in original order
 */
export function getCustomerDiscounts(
  discounts: QueryDiscount[],
): QueryDiscount[] {
  return filterDiscountsByScope(discounts, DiscountScopeValues.customer)
}

/**
 * Get all subscription-level discounts from a discount array
 * @param discounts - Array of discounts
 * @returns Array of subscription-level discounts sorted by ordinal
 */
export function getSubscriptionDiscounts(
  discounts: QueryDiscount[],
): QueryDiscount[] {
  return filterDiscountsByScope(discounts, DiscountScopeValues.subscription)
}

/**
 * Apply a single discount to a price amount
 * @param price - Base price to apply discount to
 * @param discount - Discount to apply (percentage or amount)
 * @returns Discounted price (never below 0)
 */
export function applySingleDiscount(
  price: number,
  discount: QueryDiscount,
): number {
  if (discount.__typename === 'PercentDiscount') {
    return price * (1 - discount.percentOff / 100)
  } else if (discount.__typename === 'AmountDiscount') {
    // Ensure price doesn't go below 0
    return Math.max(0, price - discount.amountOff)
  }
  return price
}

/**
 * Calculate the total discount value for a given base price
 * @param basePrice - Original price before discounts
 * @param discounts - Array of discounts to apply
 * @param scope - Optional scope filter (customer/subscription)
 * @returns Total discount amount
 */
export function calculateTotalDiscountValue(
  basePrice: number,
  discounts: QueryDiscount[],
  scope?: DiscountScope,
): number {
  const relevantDiscounts = scope
    ? filterDiscountsByScope(discounts, scope)
    : discounts

  let currentPrice = basePrice
  for (const discount of relevantDiscounts) {
    const discountedPrice = applySingleDiscount(currentPrice, discount)
    currentPrice = discountedPrice
  }

  return basePrice - currentPrice
}

/**
 * Apply multiple discounts to a price in the correct order
 * @param basePrice - Original price before discounts
 * @param discounts - Array of discounts to apply
 * @param scope - Optional scope filter (customer/subscription)
 * @returns Final discounted price
 */
export function applyMultipleDiscounts(
  basePrice: number,
  discounts: QueryDiscount[],
  scope?: DiscountScope,
): number {
  const relevantDiscounts = scope
    ? filterDiscountsByScope(discounts, scope)
    : discounts

  let currentPrice = basePrice
  for (const discount of relevantDiscounts) {
    currentPrice = applySingleDiscount(currentPrice, discount)
  }

  return currentPrice
}

/**
 * Apply all relevant discounts to a base price calculation
 * @param baseCalculation - Original calculation before discounts
 * @param discounts - Array of discounts to apply
 * @param scope - Optional scope filter (customer/subscription)
 * @returns Enhanced calculation with discount information
 */
export function applyDiscountsToCalculation(
  baseCalculation: PlanCalculation,
  discounts: QueryDiscount[],
  scope?: DiscountScope,
): DiscountedPlanCalculation {
  const relevantDiscounts = scope
    ? filterDiscountsByScope(discounts, scope)
    : discounts

  if (relevantDiscounts.length === 0) {
    return baseCalculation
  }

  const discountedPrice = applyMultipleDiscounts(
    baseCalculation.totalPrice,
    discounts,
    scope,
  )

  // Calculate the total discount amount
  const totalDiscountValue = baseCalculation.totalPrice - discountedPrice

  return {
    ...baseCalculation,
    totalPrice: discountedPrice,
    discountValue: totalDiscountValue,
    // Keep original base price for reference
    originalPrice: baseCalculation.totalPrice,
  }
}

/**
 * Calculate the effective discount percentage for a set of discounts
 * @param basePrice - Original price before discounts
 * @param discounts - Array of discounts to apply
 * @param scope - Optional scope filter (customer/subscription)
 * @returns Effective discount percentage (0-100)
 */
export function calculateEffectiveDiscountPercentage(
  basePrice: number,
  discounts: QueryDiscount[],
  scope?: DiscountScope,
): number {
  if (basePrice === 0) return 0

  const discountValue = calculateTotalDiscountValue(basePrice, discounts, scope)
  return (discountValue / basePrice) * 100
}

/**
 * Check if a discount is valid based on type and required fields
 * @param discount - Discount to validate
 * @returns True if discount is valid, false otherwise
 */
export function isValidDiscount(discount: QueryDiscount): boolean {
  if (discount.__typename === 'PercentDiscount') {
    return (
      typeof discount.percentOff === 'number' &&
      discount.percentOff >= 0 &&
      discount.percentOff <= 100
    )
  } else if (discount.__typename === 'AmountDiscount') {
    return typeof discount.amountOff === 'number' && discount.amountOff >= 0
  }
  return false
}

/**
 * Filter out invalid discounts from an array
 * @param discounts - Array of discounts to validate
 * @returns Array of valid discounts only
 */
export function filterValidDiscounts(
  discounts: QueryDiscount[],
): QueryDiscount[] {
  return discounts.filter(isValidDiscount)
}
